import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  Plus, Pencil, Trash2, X, Check, Loader2,
  Eye, EyeOff, Tag
} from 'lucide-react';

import { BACKEND_URL } from '../config/api';

const AdminCategories = () => {
  const { getAuthHeaders } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/categories`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.clone().json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/categories`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ label: newLabel.trim() })
      });
      if (res.ok) {
        setNewLabel('');
        setAdding(false);
        fetchCategories();
      } else {
        const data = await res.clone().json();
        setError(data.detail || 'Failed to add category');
      }
    } catch {
      setError('Failed to add category');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (categoryId) => {
    if (!editLabel.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/categories/${categoryId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ label: editLabel.trim() })
      });
      if (res.ok) {
        setEditingId(null);
        setEditLabel('');
        fetchCategories();
      } else {
        const data = await res.clone().json();
        setError(data.detail || 'Failed to update category');
      }
    } catch {
      setError('Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat) => {
    setError(null);
    try {
      await fetch(`${BACKEND_URL}/api/admin/categories/${cat.category_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ active: !cat.active })
      });
      fetchCategories();
    } catch {
      setError('Failed to update category');
    }
  };

  const handleDelete = async (cat) => {
    setDeleteTarget(cat);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/categories/${deleteTarget.category_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        fetchCategories();
      } else {
        const data = await res.clone().json();
        setError(data.detail || 'Failed to delete category');
      }
    } catch {
      setError('Failed to delete category');
    }
    setDeleteTarget(null);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-light text-charcoal" data-testid="categories-title">Categories</h1>
            <p className="text-sm text-gray-soft mt-1">Manage supply categories for your stash inventory</p>
          </div>
          {!adding && (
            <button
              onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-sage text-white rounded-lg text-sm hover:bg-sage-dark transition-colors"
              data-testid="add-category-btn"
            >
              <Plus size={16} />
              Add Category
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600"><X size={14} /></button>
          </div>
        )}

        {/* Add form */}
        {adding && (
          <div className="bg-white rounded-xl border border-beige p-4 flex items-center gap-3" data-testid="add-category-form">
            <Tag size={16} className="text-sage flex-shrink-0" />
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Category name..."
              className="flex-1 border border-beige rounded-lg px-3 py-2 text-sm text-charcoal focus:outline-none focus:border-sage"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              autoFocus
              data-testid="new-category-input"
            />
            <button
              onClick={handleAdd}
              disabled={saving || !newLabel.trim()}
              className="p-2 bg-sage text-white rounded-lg hover:bg-sage-dark disabled:opacity-50 transition-colors"
              data-testid="save-new-category-btn"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            </button>
            <button
              onClick={() => { setAdding(false); setNewLabel(''); }}
              className="p-2 text-gray-soft hover:text-charcoal rounded-lg transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Category list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={24} className="animate-spin text-sage" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-beige overflow-hidden">
            <div className="divide-y divide-beige">
              {categories.map((cat, index) => (
                <div
                  key={cat.category_id}
                  className={`flex items-center gap-3 px-4 py-3 group transition-colors ${
                    !cat.active ? 'bg-gray-50 opacity-60' : 'hover:bg-cream/50'
                  }`}
                  data-testid={`category-row-${cat.value}`}
                >
                  {/* Icon */}
                  <Tag size={14} className="text-sage flex-shrink-0" />

                  {/* Label */}
                  {editingId === cat.category_id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="flex-1 border border-sage rounded-lg px-3 py-1.5 text-sm text-charcoal focus:outline-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdate(cat.category_id);
                          if (e.key === 'Escape') { setEditingId(null); setEditLabel(''); }
                        }}
                        autoFocus
                        data-testid={`edit-category-input-${cat.value}`}
                      />
                      <button
                        onClick={() => handleUpdate(cat.category_id)}
                        disabled={saving}
                        className="p-1.5 bg-sage text-white rounded-lg hover:bg-sage-dark disabled:opacity-50"
                      >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setEditLabel(''); }}
                        className="p-1.5 text-gray-soft hover:text-charcoal"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-charcoal">{cat.label}</span>
                      <span className="text-xs text-gray-soft ml-2">({cat.value})</span>
                    </div>
                  )}

                  {/* Actions */}
                  {editingId !== cat.category_id && (
                    <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleToggleActive(cat)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          cat.active ? 'text-sage hover:bg-sage/10' : 'text-gray-soft hover:bg-gray-100'
                        }`}
                        title={cat.active ? 'Deactivate' : 'Activate'}
                        data-testid={`toggle-active-${cat.value}`}
                      >
                        {cat.active ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button
                        onClick={() => { setEditingId(cat.category_id); setEditLabel(cat.label); }}
                        className="p-1.5 text-gray-soft hover:text-charcoal hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit"
                        data-testid={`edit-category-${cat.value}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(cat)}
                        className="p-1.5 text-gray-soft hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                        data-testid={`delete-category-${cat.value}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* Evergreen "Other" row */}
              <div className="flex items-center gap-3 px-4 py-3 bg-cream/30" data-testid="category-row-other">
                <Tag size={14} className="text-gray-200 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-charcoal">Other</span>
                  <span className="text-xs text-gray-soft ml-2">(always present)</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        <p className="text-xs text-gray-soft text-center">
          Categories are sorted alphabetically. "Other" is always available as a fallback.
        </p>
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Category"
        message={`Items using "${deleteTarget?.label}" will be reassigned to "Other".`}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        confirmVariant="danger"
        testId="delete-category-dialog"
      />
    </AdminLayout>
  );
};

export default AdminCategories;
